<template>
  <AppLayout 
    page-title="AI Tax Optimization" 
    page-subtitle="AI-powered tax optimization and deduction suggestions"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        AI Tax Optimization
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">AI Tax Optimization</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">AI-powered tax optimization, deduction suggestions, and automated tax planning for contractors</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Analyzing your tax data...</p>
      </div>
    </div>

    <!-- AI Optimization Overview -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Potential Savings</p>
          <p class="text-3xl font-bold text-white">${{ aiStats.potentialSavings.toLocaleString() }}</p>
          <p class="text-sm text-emerald-400">AI identified</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Deduction Opportunities</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.deductionOpportunities }}</p>
          <p class="text-sm text-blue-400">found by AI</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Optimization Score</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.optimizationScore }}%</p>
          <p class="text-sm text-amber-400">tax efficiency</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.08-.041l-.08-.08z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">AI Recommendations</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.recommendations }}</p>
          <p class="text-sm text-purple-400">action items</p>
        </div>
      </div>
    </div>

    <!-- AI Analysis Results -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">AI Tax Analysis</h2>
        <button @click="runAIAnalysis" :disabled="isAnalyzing" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors">
          {{ isAnalyzing ? 'Analyzing...' : 'Run AI Analysis' }}
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Current Tax Situation -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">Current Tax Situation</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Estimated Tax Liability</p>
                <p class="text-sm text-slate-400">Based on current deductions</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ currentTaxSituation.estimatedLiability.toLocaleString() }}</p>
                <p class="text-sm text-red-400">High</p>
              </div>
            </div>

            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Current Deductions</p>
                <p class="text-sm text-slate-400">Total claimed deductions</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ currentTaxSituation.currentDeductions.toLocaleString() }}</p>
                <p class="text-sm text-yellow-400">Moderate</p>
              </div>
            </div>

            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Tax Efficiency</p>
                <p class="text-sm text-slate-400">Current optimization level</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">{{ currentTaxSituation.taxEfficiency }}%</p>
                <p class="text-sm text-yellow-400">Room for improvement</p>
              </div>
            </div>
          </div>
        </div>

        <!-- AI Optimized Scenario -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">AI Optimized Scenario</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between p-4 bg-emerald-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Optimized Tax Liability</p>
                <p class="text-sm text-slate-400">With AI suggestions</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ optimizedScenario.optimizedLiability.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">Reduced</p>
              </div>
            </div>

            <div class="flex items-center justify-between p-4 bg-emerald-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Additional Deductions</p>
                <p class="text-sm text-slate-400">AI-identified opportunities</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ optimizedScenario.additionalDeductions.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">+{{ optimizedScenario.deductionIncrease }}%</p>
              </div>
            </div>

            <div class="flex items-center justify-between p-4 bg-emerald-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Tax Savings</p>
                <p class="text-sm text-slate-400">Potential annual savings</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ optimizedScenario.taxSavings.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">+{{ optimizedScenario.savingsPercentage }}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Recommendations -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">AI Recommendations</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="recommendation in aiRecommendations" :key="recommendation.id" class="p-4 bg-slate-700/30 rounded-lg">
          <div class="flex items-start space-x-3">
            <div :class="`w-8 h-8 ${recommendation.priority === 'high' ? 'bg-red-500/20' : recommendation.priority === 'medium' ? 'bg-yellow-500/20' : 'bg-blue-500/20'} rounded-lg flex items-center justify-center`">
              <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-1">
              <h4 class="text-white font-medium mb-1">{{ recommendation.title }}</h4>
              <p class="text-sm text-slate-400 mb-2">{{ recommendation.description }}</p>
              <div class="flex items-center justify-between">
                <span :class="getPriorityClass(recommendation.priority)" class="px-2 py-1 rounded-full text-xs font-medium">
                  {{ recommendation.priority }}
                </span>
                <p class="text-sm text-emerald-400 font-medium">${{ recommendation.potentialSavings.toLocaleString() }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Deduction Opportunities -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">Deduction Opportunities</h2>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Category</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Description</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Potential Amount</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Confidence</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="opportunity in deductionOpportunities" :key="opportunity.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
              <td class="py-4 px-4">
                <span :class="getCategoryClass(opportunity.category)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ opportunity.category }}
                </span>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-medium">{{ opportunity.description }}</p>
                <p class="text-sm text-slate-400">{{ opportunity.reasoning }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-bold">${{ opportunity.potentialAmount.toLocaleString() }}</p>
              </td>
              <td class="py-4 px-4">
                <div class="flex items-center space-x-2">
                  <div class="w-16 bg-slate-600 rounded-full h-2">
                    <div :class="getConfidenceClass(opportunity.confidence)" :style="{ width: `${opportunity.confidence}%` }" class="h-2 rounded-full"></div>
                  </div>
                  <span class="text-sm text-slate-400">{{ opportunity.confidence }}%</span>
                </div>
              </td>
              <td class="py-4 px-4">
                <div class="flex space-x-2">
                  <button @click="applyDeduction(opportunity)" class="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-lg transition-colors">
                    Apply
                  </button>
                  <button @click="viewDetails(opportunity)" class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors">
                    Details
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Tax Planning Calendar -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Tax Planning Calendar</h3>
        <div class="space-y-4">
          <div v-for="event in taxPlanningEvents" :key="event.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div class="flex items-center space-x-3">
              <div :class="`w-8 h-8 ${getEventTypeClass(event.type)} rounded-lg flex items-center justify-center`">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">{{ event.title }}</p>
                <p class="text-sm text-slate-400">{{ formatDate(event.date) }}</p>
              </div>
            </div>
            <button @click="scheduleEvent(event)" class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors">
              Schedule
            </button>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">AI Tax Insights</h3>
        <div class="space-y-4">
          <div v-for="insight in aiInsights" :key="insight.id" class="p-4 bg-slate-700/30 rounded-lg">
            <div class="flex items-start space-x-3">
              <div class="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.08-.041l-.08-.08z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">{{ insight.title }}</p>
                <p class="text-sm text-slate-400">{{ insight.description }}</p>
                <p class="text-xs text-emerald-400 mt-1">{{ insight.impact }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Analysis Modal -->
    <div v-if="showAnalysisModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-2xl">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">AI Tax Analysis Results</h3>
          <button @click="showAnalysisModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="space-y-6">
          <div class="grid grid-cols-2 gap-4">
            <div class="text-center p-4 bg-slate-700/30 rounded-lg">
              <p class="text-sm text-slate-400 mb-1">Current Tax Liability</p>
              <p class="text-2xl font-bold text-white">${{ analysisResults.currentLiability.toLocaleString() }}</p>
            </div>
            <div class="text-center p-4 bg-emerald-700/30 rounded-lg">
              <p class="text-sm text-slate-400 mb-1">Optimized Liability</p>
              <p class="text-2xl font-bold text-white">${{ analysisResults.optimizedLiability.toLocaleString() }}</p>
            </div>
          </div>
          
          <div class="text-center p-4 bg-emerald-500/20 rounded-lg">
            <p class="text-lg font-bold text-emerald-400">Potential Annual Savings</p>
            <p class="text-3xl font-bold text-white">${{ analysisResults.annualSavings.toLocaleString() }}</p>
          </div>
          
          <div>
            <h4 class="text-lg font-semibold text-white mb-3">Key Recommendations</h4>
            <div class="space-y-2">
              <div v-for="rec in analysisResults.recommendations" :key="rec.id" class="flex items-center space-x-3 p-3 bg-slate-700/30 rounded-lg">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <p class="text-white">{{ rec.description }}</p>
                <span class="text-sm text-emerald-400 font-medium">${{ rec.savings.toLocaleString() }}</span>
              </div>
            </div>
          </div>
          
          <div class="flex space-x-3">
            <button @click="showAnalysisModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Close
            </button>
            <button @click="applyAllRecommendations" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Apply All Recommendations
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Types
interface AIStats {
  potentialSavings: number
  deductionOpportunities: number
  optimizationScore: number
  recommendations: number
}

interface CurrentTaxSituation {
  estimatedLiability: number
  currentDeductions: number
  taxEfficiency: number
}

interface OptimizedScenario {
  optimizedLiability: number
  additionalDeductions: number
  deductionIncrease: number
  taxSavings: number
  savingsPercentage: number
}

interface AIRecommendation {
  id: number
  title: string
  description: string
  priority: string
  potentialSavings: number
}

interface DeductionOpportunity {
  id: number
  category: string
  description: string
  reasoning: string
  potentialAmount: number
  confidence: number
}

interface TaxPlanningEvent {
  id: number
  title: string
  date: string
  type: string
}

interface AIInsight {
  id: number
  title: string
  description: string
  impact: string
}

interface AnalysisResults {
  currentLiability: number
  optimizedLiability: number
  annualSavings: number
  recommendations: Array<{
    id: number
    description: string
    savings: number
  }>
}

// Reactive data
const isLoading = ref(true)
const isAnalyzing = ref(false)
const showAnalysisModal = ref(false)

const aiStats = ref<AIStats>({
  potentialSavings: 0,
  deductionOpportunities: 0,
  optimizationScore: 0,
  recommendations: 0
})

const currentTaxSituation = ref<CurrentTaxSituation>({
  estimatedLiability: 0,
  currentDeductions: 0,
  taxEfficiency: 0
})

const optimizedScenario = ref<OptimizedScenario>({
  optimizedLiability: 0,
  additionalDeductions: 0,
  deductionIncrease: 0,
  taxSavings: 0,
  savingsPercentage: 0
})

const aiRecommendations = ref<AIRecommendation[]>([])
const deductionOpportunities = ref<DeductionOpportunity[]>([])
const taxPlanningEvents = ref<TaxPlanningEvent[]>([])
const aiInsights = ref<AIInsight[]>([])
const analysisResults = ref<AnalysisResults>({
  currentLiability: 0,
  optimizedLiability: 0,
  annualSavings: 0,
  recommendations: []
})

// API Methods
const fetchAITaxData = async () => {
  try {
    isLoading.value = true
    
    // Fetch AI tax statistics
    const statsResponse = await fetch('/api/ai-tax/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      aiStats.value = statsData
    }
    
    // Fetch current tax situation
    const currentResponse = await fetch('/api/ai-tax/current-situation', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (currentResponse.ok) {
      const currentData = await currentResponse.json()
      currentTaxSituation.value = currentData
    }
    
    // Fetch optimized scenario
    const optimizedResponse = await fetch('/api/ai-tax/optimized-scenario', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (optimizedResponse.ok) {
      const optimizedData = await optimizedResponse.json()
      optimizedScenario.value = optimizedData
    }
    
    // Fetch AI recommendations
    const recommendationsResponse = await fetch('/api/ai-tax/recommendations', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (recommendationsResponse.ok) {
      const recommendationsData = await recommendationsResponse.json()
      aiRecommendations.value = recommendationsData
    }
    
    // Fetch deduction opportunities
    const opportunitiesResponse = await fetch('/api/ai-tax/deduction-opportunities', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (opportunitiesResponse.ok) {
      const opportunitiesData = await opportunitiesResponse.json()
      deductionOpportunities.value = opportunitiesData
    }
    
    // Fetch tax planning events
    const eventsResponse = await fetch('/api/ai-tax/planning-events', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (eventsResponse.ok) {
      const eventsData = await eventsResponse.json()
      taxPlanningEvents.value = eventsData
    }
    
    // Fetch AI insights
    const insightsResponse = await fetch('/api/ai-tax/insights', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (insightsResponse.ok) {
      const insightsData = await insightsResponse.json()
      aiInsights.value = insightsData
    }
    
  } catch (error) {
    console.error('Error fetching AI tax data:', error)
    // Fallback to empty data
    aiStats.value = {
      potentialSavings: 0,
      deductionOpportunities: 0,
      optimizationScore: 0,
      recommendations: 0
    }
    currentTaxSituation.value = {
      estimatedLiability: 0,
      currentDeductions: 0,
      taxEfficiency: 0
    }
    optimizedScenario.value = {
      optimizedLiability: 0,
      additionalDeductions: 0,
      deductionIncrease: 0,
      taxSavings: 0,
      savingsPercentage: 0
    }
    aiRecommendations.value = []
    deductionOpportunities.value = []
    taxPlanningEvents.value = []
    aiInsights.value = []
  } finally {
    isLoading.value = false
  }
}

// Methods
const runAIAnalysis = async () => {
  try {
    isAnalyzing.value = true
    
    const response = await fetch('/api/ai-tax/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      const results = await response.json()
      analysisResults.value = results
      showAnalysisModal.value = true
      await fetchAITaxData() // Refresh data
    } else {
      alert('Error running AI analysis')
    }
  } catch (error) {
    console.error('Error running AI analysis:', error)
    alert('Error running AI analysis')
  } finally {
    isAnalyzing.value = false
  }
}

const applyDeduction = async (opportunity: DeductionOpportunity) => {
  try {
    const response = await fetch(`/api/ai-tax/apply-deduction/${opportunity.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      alert('Deduction applied successfully!')
      await fetchAITaxData() // Refresh data
    } else {
      alert('Error applying deduction')
    }
  } catch (error) {
    console.error('Error applying deduction:', error)
    alert('Error applying deduction')
  }
}

const applyAllRecommendations = async () => {
  try {
    const response = await fetch('/api/ai-tax/apply-all-recommendations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      alert('All recommendations applied successfully!')
      showAnalysisModal.value = false
      await fetchAITaxData() // Refresh data
    } else {
      alert('Error applying recommendations')
    }
  } catch (error) {
    console.error('Error applying recommendations:', error)
    alert('Error applying recommendations')
  }
}

const viewDetails = (opportunity: DeductionOpportunity) => {
  console.log('Viewing details for:', opportunity)
  alert(`Details for ${opportunity.description}`)
}

const scheduleEvent = (event: TaxPlanningEvent) => {
  console.log('Scheduling event:', event)
  alert(`Scheduling ${event.title} for ${formatDate(event.date)}`)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getPriorityClass = (priority: string) => {
  const classes: { [key: string]: string } = {
    'high': 'bg-red-500/90',
    'medium': 'bg-yellow-500/90',
    'low': 'bg-blue-500/90'
  }
  return classes[priority] || 'bg-slate-500/90'
}

const getCategoryClass = (category: string) => {
  const classes: { [key: string]: string } = {
    'business': 'bg-blue-500/90',
    'vehicle': 'bg-green-500/90',
    'home': 'bg-purple-500/90',
    'equipment': 'bg-amber-500/90',
    'insurance': 'bg-cyan-500/90',
    'other': 'bg-slate-500/90'
  }
  return classes[category] || 'bg-slate-500/90'
}

const getConfidenceClass = (confidence: number) => {
  if (confidence >= 80) return 'bg-emerald-500'
  if (confidence >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

const getEventTypeClass = (type: string) => {
  const classes: { [key: string]: string } = {
    'payment': 'bg-red-500/20',
    'deadline': 'bg-yellow-500/20',
    'planning': 'bg-blue-500/20',
    'review': 'bg-purple-500/20'
  }
  return classes[type] || 'bg-slate-500/20'
}

// Load data on mount
onMounted(() => {
  fetchAITaxData()
})
</script>

<style scoped>
.ai-tax-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>
